// Google Analytics 4 wiring for the marketing site.
//
// Activation:
//   1. Set REACT_APP_GA_MEASUREMENT_ID at build time (e.g. "G-XXXXXXXXXX").
//      Amplify Console → App settings → Environment variables.
//   2. The user accepts the CookieBanner (sets pa_cookie_accepted in
//      localStorage). Until then we stay dormant — no script load, no
//      cookies dropped, nothing.
//
// SPA tracking: useAnalytics() fires page_view on every react-router
// location change; without that GA4 would only see the initial load
// because the page never refreshes.

const GA_ID: string = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
const CONSENT_KEY = "pa_cookie_accepted";

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
    }
}

let initialized = false;

const hasConsent = (): boolean => {
    try {
        return !!window.localStorage?.getItem(CONSENT_KEY);
    } catch {
        return false;
    }
};

// Inject the gtag script and run the standard GA4 boot sequence.
// Idempotent — re-calling does nothing once initialised.
export const initAnalytics = (): void => {
    if (initialized) return;
    if (!GA_ID) return;
    if (typeof window === "undefined") return;
    if (!hasConsent()) return;

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_ID)}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    // gtag is a thin wrapper that pushes arguments into dataLayer.
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    // anonymize_ip + no advertising features = lighter-weight, GDPR-friendlier.
    window.gtag("config", GA_ID, {
        anonymize_ip: true,
        allow_google_signals: false,
    });

    initialized = true;
};

export const trackPageView = (path: string, title?: string): void => {
    if (!initialized) return;
    window.gtag("event", "page_view", {
        page_path: path,
        page_title: title || (typeof document !== "undefined" ? document.title : undefined),
    });
};

export const isAnalyticsConfigured = (): boolean => !!GA_ID;
